import React, {useEffect, useRef, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { initializeAuth, getLastestValidations, getValidationById, getValidationsByFilter } from "../../services/apiService";
import { setSelectedItem, setValidations } from '../../redux/dataSlice';
import { useMsal } from "@azure/msal-react";
import { ResultsPercentages } from './ResultsPercentages';
import useResizeObserver from '../../utils/useResizeObserver';

export const ValidationsList = () => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [divRef, dimensions] = useResizeObserver();
  const { instance, accounts } = useMsal();
  const { autoRefresh, validations } = useSelector(state => state.data);
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState(0);
  const refreshTime = 2000;
  const intervalRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoading && dimensions.height > 0) {
      fetchLatestValidations();
    }
  }, [dimensions, currentPage]); // Recalculate when data changes

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchLatestValidations();
      }, refreshTime);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  },[autoRefresh])

  const fetchLatestValidations = async () => {
    setIsLoading(true);
    await initializeAuth(instance, accounts);
    try {
      const currHeight = divRef.current.clientHeight ? divRef.current.clientHeight - 258 : 0;
      const rows = Math.floor(currHeight / 40);
      const lastValidations = await getLastestValidations(currentPage, rows);
      dispatch(setValidations(lastValidations));
      setPages(Math.ceil((lastValidations.totalCount || 0) / rows));
    } catch (error) {
      console.error("Failed to fetch latest validations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemSelect = async (validationId) => {
    if (validationId) {
        dispatch(setSelectedItem(null));
        console.log("Selected validation:", validationId);
        await initializeAuth(instance, accounts);
        const response = await getValidationById(validationId);
        dispatch(setSelectedItem(response));      
    }
  };
    return (
    <div className="flex flex-col position-relative v-full" >
      <div className='h-screen' ref={divRef}>
        <table className="w-full overflow-hidden table-auto">
            <colgroup>
              <col style={{width: "25%"}} />
              <col style={{width: "15%"}} />
              <col style={{width: "30%"}} />
              <col style={{width: "30%"}} />
            </colgroup>
            <thead>
            <tr>
              <th className="text-left p-2 border-b-2 border-gray-300">Name</th>
              <th className="text-left p-2 border-b-2 border-gray-300">Format</th>
              <th className="text-left p-2 border-b-2 border-gray-300">Execution Date</th>
              <th className="text-left p-2 border-b-2 border-gray-300">Results</th>
            </tr>
          </thead>
          <tbody>
            {validations && validations.reports && validations.reports.map((validation, idx) => (
              <tr key={idx} className="hover:bg-gray-100 cursor-pointer" onClick={() => handleItemSelect(validation.runId)}>
                <td className="p-2 border-b border-gray-200">{validation.formatName}</td>
                <td className="p-2 border-b border-gray-200">{validation.formatNumber}</td>
                <td className="p-2 border-b border-gray-200">{new Date(validation.executionDate).toLocaleString()}</td>
                <td className="p-2 border-b border-gray-200">
                  <ResultsPercentages results={validation} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pl-4 h-[112px] position-absolute b-90 bg-white w-full">
        <div className="flex items-center gap-2 mt-4">
          <button className="border rounded p-1" disabled={currentPage <= 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>&lt;&lt;</button>
          <button className="border rounded p-1" disabled={currentPage <= 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>&lt;</button>
          <button className="border rounded p-1" disabled={currentPage >= pages} onClick={() => setCurrentPage(prev => Math.min(pages, prev + 1))}>&gt;</button>
          <button className="border rounded p-1" disabled={currentPage >= pages} onClick={() => setCurrentPage(pages)}>&gt;&gt;</button>
          <span className="flex items-center gap-1"><div>Page</div><strong>{currentPage} of {pages}</strong></span>
          <span className="flex items-center gap-1">| Go to page:
            <input type="number" min="1" max={pages} className="border p-1 rounded w-16" value={currentPage} onChange={(e) => setCurrentPage(parseInt(e.target.value) || 1)} /></span>
          <span className="flex items-center gap-1">{validations.totalCount} Rows</span>
        </div>
      </div>
    </div>
  )
};
